import SystemError from "@/commons/models/SystemError";

/**
 * 日付関連の共通処理クラス
 *
 */
export default class DateUtil {
  /**
   * 曜日文言Map
   * 言語名をKeyに、日～土を表す文言配列をvalueで持つ
   */
  private weeksMap: Map<string, Array<string>> = new Map<string, Array<string>>([
    ["en", ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]],
    ["ja", ["日", "月", "火", "水", "木", "金", "土"]]
  ]);

  /**
   * 数値型の日付を文字列に変換する
   * 渡されたフォーマットに含まれる特定の文字列に対して置換を行うことで実現している
   * そのため、フォーマット文字列は予約語扱いなので注意
   *
   * フォーマット文字列
   * ・year："yyyy"
   * ・month: "mm"
   * ・day: "dd"
   * ・hour: "hh"
   * ・minute: "MM"
   * ・second: "ss"
   *
   * @param date 日付(yyyymmddhhMMss)
   * @param format フォーマット文字列
   */
  public convertNumberToString(date: number, format: string, zeroPadding: boolean = true): string {
    if (!date) {
      throw new SystemError("DateUtil.convertNumberToString() failed convert. date is null or undefined.");
    }

    if (isNaN(date)) {
      throw new SystemError("DateUtil.convertNumberToString() failed convert. date is not a number.");
    }

    const dateString = String(date);
    const dateObject: any = {
      year: dateString.substr(0, 4),
      month: dateString.substr(4, 2),
      day: dateString.substr(6, 2),
      hour: dateString.substr(8, 2),
      minute: dateString.substr(10, 2),
      second: dateString.substr(12, 2)
    };

    const formatedString: string = this.formatString(dateObject, format, zeroPadding);

    return formatedString;
  }

  /**
   * Date型の日付を文字列に変換する
   * 渡されたフォーマットに含まれる特定の文字列に対して置換を行うことで実現している
   * そのため、フォーマット文字列は予約語扱いなので注意
   *
   * フォーマット文字列
   * ・year："yyyy"
   * ・month: "mm"
   * ・day: "dd"
   * ・hour: "hh"
   * ・minute: "MM"
   * ・second: "ss"
   *
   * @param date 日付
   * @param format フォーマット文字列
   */
  public convertDateToString(date: Date, format: string, zeroPadding: boolean = true): string {
    if (!date) {
      throw new SystemError("DateUtil.convertDateToString() failed convert. date is null or undefined.");
    }

    if (!(date instanceof Date)) {
      throw new SystemError("DateUtil.convertDateToString() failed convert. date is not typeof Date.");
    }

    // Date型の年月日時分秒を文字列になおす。年以外は1桁がありえるので0埋めする
    const year = date.getFullYear().toString();
    let month = (date.getMonth() + 1).toString();
    if (month.length == 1) {
      month = "0" + month;
    }
    let day = date.getDate().toString();
    if (day.length == 1) {
      day = "0" + day;
    }
    let hour = date.getHours().toString();
    if (hour.length == 1) {
      hour = "0" + hour;
    }
    let minute = date.getMinutes().toString();
    if (minute.length == 1) {
      minute = "0" + minute;
    }
    let second = date.getSeconds().toString();
    if (second.length == 1) {
      second = "0" + second;
    }

    const dateObject: any = {
      year: year,
      month: month,
      day: day,
      hour: hour,
      minute: minute,
      second: second
    };

    const formatedString: string = this.formatString(dateObject, format, zeroPadding);

    return formatedString;
  }

  /**
   * Date型の日付を数値に変換する
   * 渡されたフォーマットに含まれる特定の文字列に対して置換を行うことで実現している
   * そのため、フォーマット文字列は予約語扱いなので注意
   *
   * フォーマット文字列
   * ・year："yyyy"
   * ・month: "mm"
   * ・day: "dd"
   * ・hour: "hh"
   * ・minute: "MM"
   * ・second: "ss"
   *
   * @param date 日付
   * @param format フォーマット文字列
   */
  public convertDateToNumber(date: Date, format: string): number {
    let convertedDate: number = 0;

    convertedDate = Number(this.convertDateToString(date, format));

    if (Number.isNaN(convertedDate)) {
      throw new SystemError("DateUtil.convertDateToNumber() failed convert. converted date is NaN.");
    }

    return convertedDate;
  }

  /**
   * yyyymmddhhMMss形式の日付を表す数値をDateオブジェクトに変換する
   *
   * @param date
   */
  public convertNumberToDate(date: number): Date {
    if (!date) {
      throw new SystemError("DateUtil.convertNumberToDate() failed convert. date is null or undefined.");
    }

    if (isNaN(date)) {
      throw new SystemError("DateUtil.convertNumberToDate() failed convert. date is not a number.");
    }

    const dateString = String(date);
    // yyyymmddhhMMssの14桁の変換
    if (dateString.length == 14) {
      return new Date(
        Number(dateString.substr(0, 4)),
        // Dateオブジェクトの月は0 ~ 11が1 ~ 12月に対応指定しているため、-1する必要がある
        Number(dateString.substr(4, 2)) - 1,
        Number(dateString.substr(6, 2)),
        Number(dateString.substr(8, 2)),
        Number(dateString.substr(10, 2)),
        Number(dateString.substr(12, 2))
      );
    }
    // yyyymmddhhMMssSSSの17桁の変換
    else if (dateString.length == 17) {
      return new Date(
        Number(dateString.substr(0, 4)),
        // Dateオブジェクトの月は0 ~ 11が1 ~ 12月に対応指定しているため、-1する必要がある
        Number(dateString.substr(4, 2)) - 1,
        Number(dateString.substr(6, 2)),
        Number(dateString.substr(8, 2)),
        Number(dateString.substr(10, 2)),
        Number(dateString.substr(12, 2))
      );
    } else {
      throw new SystemError("DateUtil.convertNumberToDate() failed convert. date length is invalid. length:" + String(date).length);
    }
  }

  /**
   * 日付文字列から数字以外を削除する
   *
   * このメソッドでは日付部分等の0埋めは行わないので、日付等の0埋めは事前に行っておくこと
   *
   * @param date 文字列(/や月等)を含んだ日付文字列
   */
  public replaceNonNumeric(date: string): string {
    if (!date) {
      throw new SystemError("DateUtil.replaceNonNumeric() failed convert. date is null or undefined.");
    }

    if (typeof date != "string") {
      throw new SystemError("DateUtil.replaceNonNumeric() failed convert. date is not a string.");
    }

    date = date.replace(new RegExp("[^0-9]", "g"), "");
    return date;
  }

  /**
   * フォーマット文字列に年月日時分秒を置換して返す
   *
   * @param date 年月日時分秒のstringを持ったObject
   * @param format フォーマット文字列
   * @returns フォーマットした日付文字列
   */
  private formatString(date: any, format: string, zeroPadding: boolean): string {
    if (!format) {
      throw new SystemError("DateUtil.formatString() failed convert. format is null or undefined.");
    }

    if (typeof format != "string") {
      throw new SystemError("DateUtil.formatString() failed convert. format is not a string.");
    }

    let formatedString: string = format.replace("yyyy", date.year);

    if (zeroPadding) {
      formatedString = formatedString.replace("mm", date.month);
      formatedString = formatedString.replace("dd", date.day);
      formatedString = formatedString.replace("hh", date.hour);
      formatedString = formatedString.replace("MM", date.minute);
      formatedString = formatedString.replace("ss", date.second);
    } else {
      formatedString = date.month[0] == "0" ? formatedString.replace("mm", date.month[1]) : formatedString.replace("mm", date.month);
      formatedString = date.day[0] == "0" ? formatedString.replace("dd", date.day[1]) : formatedString.replace("dd", date.day);
      formatedString = date.hour[0] == "0" ? formatedString.replace("hh", date.hour[1]) : formatedString.replace("hh", date.hour);
      formatedString = date.minute[0] == "0" ? formatedString.replace("MM", date.minute[1]) : formatedString.replace("MM", date.minute);
      formatedString = date.second[0] == "0" ? formatedString.replace("ss", date.second[1]) : formatedString.replace("ss", date.second);

      // formatedString = date.minute[0] == "0" ? formatedString.replace("MM", date.minute[1]) : formatedString.replace("MM", date.minute);
      // formatedString = date.second[0] == "0" ? formatedString.replace("ss", date.second[1]) : formatedString.replace("ss", date.second);
      // 分秒は0埋めしない
      // formatedString = formatedString.replace("MM", date.minute);
      // formatedString = formatedString.replace("ss", date.second);
    }

    return formatedString;
  }

  /**
   * Date型の日付から曜日文字列を作成する
   *
   * @param date Date型の日付
   * @param language 言語コード文字列
   * @returns 曜日文字列
   */
  public getWeekName(date: Date, language: string): string {
    if (!date) {
      throw new SystemError("DateUtil.getWeekName() failed convert. date is null or undefined.");
    }

    if (!(date instanceof Date)) {
      throw new SystemError("DateUtil.getWeekName() failed convert. date is not typeof Date.");
    }

    const weeks: Array<string> | undefined = this.weeksMap.get(language);
    if (!weeks) {
      throw new SystemError("DateUtil.getWeekName() failed convert. lang is undefind code.");
    }

    const week: string = weeks[date.getDay()];

    return week;
  }

  /**
   * 「hh:mm」形式の時間を0埋めして返す
   */
  public time0Padding(time: string): string {
    let paddingedTime: string = "";

    // 渡された時間は「hh:mm」形式のはずなので、「:」で分割し、0埋めして結合する
    // 間違った形式の場合は「:」で分割できないので何もしない
    const splitTime: Array<string> = time.split(":");

    if (splitTime.length == 2) {
      if (splitTime[0].length == 1) {
        splitTime[0] = "0" + splitTime[0];
      }
      if (splitTime[1].length == 1) {
        splitTime[1] = "0" + splitTime[1];
      }

      paddingedTime = splitTime[0] + ":" + splitTime[1];
    }

    return paddingedTime;
  }

  /**
   * 生年月日から年齢に変換
   *
   * @param birthday 生年月日
   */
  public convertBirthdayToAge(year: number, month: number, date: number): number {
    const today: Date = new Date();
    const thisYearsBirthday = new Date(today.getFullYear(), month - 1, date);
    let age = today.getFullYear() - year;

    if (today < thisYearsBirthday) {
      //今年まだ誕生日が来ていない
      age--;
    }

    return age;
  }

  /**
   * 数値の日付を時刻文字列に変換する
   * - 時間の先頭が0なら消す
   *   画面仕様では時刻は0埋めするルールだが、timepickerは0埋めしない仕様なので必要
   *   bootstrap-timepickerは0埋めするoptionがない
   */
  public convertTimepickerTime(date: number): string {
    const time = this.convertNumberToString(date, "hh:MM");
    if (time.length == 5 && time[0] == "0") {
      return time.substr(1, time.length - 1);
    } else {
      return time;
    }
  }

  /**
   * 数値型の日付が実際に存在する日付かチェックする
   *
   * - 存在すればtrue、存在しなければfalseを返す
   */
  public checkExistDate(date: number): boolean {
    // Date型にする
    const _date: string = String(date);
    const year: number = Number(_date.substr(0, 4));
    const month: number = Number(_date.substr(4, 2)) - 1;
    const day: number = Number(_date.substr(6, 2));
    const targetDate: Date = new Date(year, month, day);

    // Date型に変換後、年月日を比較して異なる場合はエラー
    if (targetDate.getFullYear() != year) {
      return false;
    } else if (targetDate.getMonth() != month) {
      return false;
    } else if (targetDate.getDate() != day) {
      return false;
    }

    return true;
  }

  /**
   * ### 日付を比較して差分の日数を返す（時間考慮：有）
   *
   * - 例：2024-07-20T10:30:00と2024-07-25T09:00:00であれば4日を返す
   */
  public diffDatetime(date1: Date, date2: Date): number {
    const diffInMilliSeconds = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffInMilliSeconds / (1000 * 60 * 60 * 24));
  }

  /**
   * ### 日付を比較して差分の日数を返す（時間考慮：無）
   *
   * - 例：2024-07-20T10:30:00と2024-07-25T09:00:00であれば4日を返す
   */
  public diffDate(date1: Date, date2: Date): number {
    const startOfDayDate1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const startOfDayDate2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

    const diffInMilliseconds = Math.abs(startOfDayDate2.getTime() - startOfDayDate1.getTime());
    return Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  }
}
